/**
 * SpeakerTranscriptionPanel - Admin/Speaker component for broadcasting live transcription
 * Features:
 * - Real-time speech capture using Web Speech API
 * - Broadcasting to all attendees via API
 * - Live preview of sign language interpretation
 * - Session management (start/stop)
 * - Multiple language support
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Mic, MicOff, Radio, RadioOff, Users, Clock, FileText,
  Volume2, Waves, Zap, Settings, Eye, Send, Wifi, WifiOff,
  Play, Square, AlertCircle, CheckCircle2, Hand
} from 'lucide-react';
import SignLanguageAvatarLive, { SIGN_LANGUAGE_CONFIG } from './SignLanguageAvatarLive';
import { getToken } from '@/lib/authToken';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Speech recognition language codes
const SPEECH_LANGUAGES = [
  { value: 'en-US', label: '🇺🇸 English (US)', short: 'English' },
  { value: 'en-GB', label: '🇬🇧 English (UK)', short: 'English UK' },
  { value: 'es-ES', label: '🇪🇸 Spanish', short: 'Spanish' },
  { value: 'fr-FR', label: '🇫🇷 French', short: 'French' },
  { value: 'de-DE', label: '🇩🇪 German', short: 'German' },
  { value: 'pt-BR', label: '🇧🇷 Portuguese', short: 'Portuguese' },
  { value: 'ja-JP', label: '🇯🇵 Japanese', short: 'Japanese' },
  { value: 'zh-CN', label: '🇨🇳 Chinese', short: 'Chinese' },
  { value: 'ko-KR', label: '🇰🇷 Korean', short: 'Korean' },
  { value: 'ru-RU', label: '🇷🇺 Russian', short: 'Russian' },
  { value: 'it-IT', label: '🇮🇹 Italian', short: 'Italian' },
  { value: 'ar-SA', label: '🇸🇦 Arabic', short: 'Arabic' },
  { value: 'sw-KE', label: '🇰🇪 Swahili', short: 'Swahili' },
  { value: 'zu-ZA', label: '🇿🇦 Zulu', short: 'Zulu' },
];

const SpeakerTranscriptionPanel = ({
  eventId,
  onClose,
  defaultSignLanguage = 'asl',
}) => {
  // Session state
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  
  // Transcription state
  const [currentText, setCurrentText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [transcriptHistory, setTranscriptHistory] = useState([]);
  
  // Settings
  const [speechLanguage, setSpeechLanguage] = useState('en-US');
  const [signLanguage, setSignLanguage] = useState(defaultSignLanguage);
  const [speakerName, setSpeakerName] = useState('Speaker');
  const [autobroadcast, setAutobroadcast] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  
  // Stats
  const [stats, setStats] = useState({
    totalWords: 0,
    duration: 0,
    viewers: 0,
  });
  const [confidence, setConfidence] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Refs
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const sessionStartRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const broadcastTimeoutRef = useRef(null);
  
  // Get auth token — delegate to /lib/authToken which handles all sources.
  const getAuthToken = () => getToken() || '';

  // Initialize audio analyser
  const initAudioAnalyser = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      const updateLevel = () => {
        if (!analyserRef.current || !isListening) {
          setAudioLevel(0);
          return;
        }
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(1, average / 128));
        
        animationRef.current = requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
      return stream;
    } catch (err) {
      console.error('Failed to init audio:', err);
      return null;
    }
  };

  // Stop audio analyser
  const stopAudioAnalyser = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
    }
    setAudioLevel(0);
  }, []);

  // Broadcast transcription to attendees
  const broadcastTranscription = useCallback(async (text, isFinal = true) => {
    if (!text.trim() || !eventId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/events/${eventId}/transcription/broadcast`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          text: text.trim(),
          speaker_name: speakerName,
          confidence,
          language: speechLanguage.split('-')[0],
          is_final: isFinal,
        }),
      });
      
      if (!response.ok) {
        console.error('Broadcast failed:', await response.text());
      }
    } catch (err) {
      console.error('Broadcast error:', err);
    }
  }, [eventId, speakerName, confidence, speechLanguage]);

  // Initialize speech recognition
  const initSpeechRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.lang = speechLanguage;

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      let maxConf = 0;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        maxConf = Math.max(maxConf, result[0].confidence || 0.8);

        if (result.isFinal) {
          final += transcript + ' ';
        } else {
          interim += transcript;
        }
      }

      setConfidence(maxConf);

      if (final.trim()) {
        setCurrentText(final.trim());
        setInterimText('');
        
        // Update stats
        const wordCount = final.trim().split(/\s+/).length;
        setStats(prev => ({
          ...prev,
          totalWords: prev.totalWords + wordCount
        }));
        
        // Add to history
        setTranscriptHistory(prev => [
          ...prev.slice(-50),
          { text: final.trim(), timestamp: Date.now(), confidence: maxConf }
        ]);
        
        // Auto-broadcast if enabled
        if (autobroadcast && isBroadcasting) {
          // Debounce broadcasting
          if (broadcastTimeoutRef.current) {
            clearTimeout(broadcastTimeoutRef.current);
          }
          broadcastTimeoutRef.current = setTimeout(() => {
            broadcastTranscription(final.trim(), true);
          }, 100);
        }
      } else if (interim) {
        setInterimText(interim);
        setCurrentText(interim);
      }
    };

    recognition.onerror = (event) => {
      console.error('Recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied');
        setIsListening(false);
        stopAudioAnalyser();
      }
    };

    recognition.onend = () => {
      if (isListening) {
        try {
          recognition.start();
        } catch (e) {}
      }
    };

    return recognition;
  }, [speechLanguage, autobroadcast, isBroadcasting, broadcastTranscription, stopAudioAnalyser, isListening]);

  // Start session
  const startSession = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/events/${eventId}/transcription/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          language: speechLanguage.split('-')[0],
          sign_language: signLanguage,
          enabled: true,
        }),
      });

      if (!response.ok) throw new Error('Failed to start session');

      setIsSessionActive(true);
      sessionStartRef.current = Date.now();
      
      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        setStats(prev => ({ ...prev, duration: elapsed }));
      }, 1000);
      
      toast.success('Transcription session started');
    } catch (err) {
      toast.error('Failed to start session');
      console.error(err);
    }
  };

  // Stop session
  const stopSession = async () => {
    try {
      await fetch(`${API_BASE}/api/admin/events/${eventId}/transcription/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
    } catch (err) {
      console.error(err);
    }

    // Stop listening
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    stopAudioAnalyser();
    
    // Clear intervals
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    
    setIsSessionActive(false);
    setIsListening(false);
    setIsBroadcasting(false);
    setCurrentText('');
    toast.info('Session ended');
  };

  // Toggle listening
  const toggleListening = async () => {
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      stopAudioAnalyser();
      setIsListening(false);
    } else {
      await initAudioAnalyser();
      
      if (!recognitionRef.current) {
        recognitionRef.current = initSpeechRecognition();
      }
      
      if (recognitionRef.current) {
        try {
          recognitionRef.current.lang = speechLanguage;
          recognitionRef.current.start();
          setIsListening(true);
        } catch (e) {
          if (e.message !== 'recognition has already started') {
            toast.error('Failed to start microphone');
          }
        }
      }
    }
  };

  // Toggle broadcasting
  const toggleBroadcasting = () => {
    setIsBroadcasting(!isBroadcasting);
    if (!isBroadcasting) {
      toast.success('Broadcasting started - attendees can now see the interpretation');
    } else {
      toast.info('Broadcasting paused');
    }
  };

  // Manual send
  const handleManualSend = () => {
    if (currentText.trim()) {
      broadcastTranscription(currentText.trim(), true);
      toast.success('Sent to attendees');
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      if (broadcastTimeoutRef.current) clearTimeout(broadcastTimeoutRef.current);
    };
  }, []);

  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const signLangConfig = SIGN_LANGUAGE_CONFIG[signLanguage] || SIGN_LANGUAGE_CONFIG.asl;

  return (
    <Card className="w-full max-w-4xl mx-auto" data-testid="speaker-transcription-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <Radio className={`h-5 w-5 ${isSessionActive ? 'text-red-500 animate-pulse' : 'text-slate-400'}`} />
              Live Transcription Broadcast
            </CardTitle>
            <CardDescription>
              Broadcast your speech as sign language to all attendees in real-time
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isSessionActive && (
              <Badge variant="destructive" className="animate-pulse gap-1">
                <span className="w-2 h-2 bg-white rounded-full animate-ping" />
                LIVE
              </Badge>
            )}
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>×</Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Session Controls */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
          <div className="flex items-center gap-4">
            {!isSessionActive ? (
              <Button 
                onClick={startSession} 
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                data-testid="start-session-btn"
              >
                <Play className="h-4 w-4" />
                Start Session
              </Button>
            ) : (
              <>
                <Button 
                  onClick={stopSession} 
                  variant="destructive"
                  className="gap-2"
                  data-testid="stop-session-btn"
                >
                  <Square className="h-4 w-4" />
                  End Session
                </Button>
                
                <Button
                  onClick={toggleListening}
                  variant={isListening ? 'secondary' : 'default'}
                  className={`gap-2 ${isListening ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : ''}`}
                  data-testid="toggle-mic-btn"
                >
                  {isListening ? (
                    <><MicOff className="h-4 w-4" /> Pause Mic</>
                  ) : (
                    <><Mic className="h-4 w-4" /> Start Mic</>
                  )}
                </Button>
                
                <Button
                  onClick={toggleBroadcasting}
                  variant={isBroadcasting ? 'secondary' : 'outline'}
                  className={`gap-2 ${isBroadcasting ? 'bg-green-100 text-green-700 hover:bg-green-200' : ''}`}
                  data-testid="toggle-broadcast-btn"
                >
                  {isBroadcasting ? (
                    <><Wifi className="h-4 w-4 animate-pulse" /> Broadcasting</>
                  ) : (
                    <><WifiOff className="h-4 w-4" /> Start Broadcast</>
                  )}
                </Button>
              </>
            )}
          </div>
          
          {isSessionActive && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-slate-600">
                <Clock className="h-4 w-4" />
                {formatDuration(stats.duration)}
              </div>
              <div className="flex items-center gap-1.5 text-slate-600">
                <FileText className="h-4 w-4" />
                {stats.totalWords} words
              </div>
              <div className="flex items-center gap-1.5 text-slate-600">
                <Users className="h-4 w-4" />
                {stats.viewers} viewers
              </div>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Controls & Preview */}
          <div className="space-y-4">
            {/* Settings */}
            <div className="p-4 bg-white border rounded-lg space-y-4">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Settings
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Speech Language</Label>
                  <Select value={speechLanguage} onValueChange={setSpeechLanguage}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SPEECH_LANGUAGES.map(lang => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Sign Language Output</Label>
                  <Select value={signLanguage} onValueChange={setSignLanguage}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SIGN_LANGUAGE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.flag} {config.name} - {config.region}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-xs">Speaker Name</Label>
                <Input
                  value={speakerName}
                  onChange={(e) => setSpeakerName(e.target.value)}
                  placeholder="Enter speaker name"
                  className="h-9"
                />
              </div>
              
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={autobroadcast}
                    onCheckedChange={setAutobroadcast}
                    id="auto-broadcast"
                  />
                  <Label htmlFor="auto-broadcast" className="text-xs">Auto-broadcast</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showPreview}
                    onCheckedChange={setShowPreview}
                    id="show-preview"
                  />
                  <Label htmlFor="show-preview" className="text-xs">Show Preview</Label>
                </div>
              </div>
            </div>

            {/* Audio Level Indicator */}
            {isListening && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-emerald-700 flex items-center gap-1.5">
                    <Waves className="h-3.5 w-3.5 animate-pulse" />
                    Audio Input
                  </span>
                  <span className="text-xs text-emerald-600">
                    {Math.round(confidence * 100)}% confidence
                  </span>
                </div>
                <div className="h-3 bg-emerald-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-75"
                    style={{ width: `${audioLevel * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Transcript History */}
            <div className="p-4 bg-white border rounded-lg">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Transcript History
              </h3>
              <ScrollArea className="h-40">
                {transcriptHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">
                    Start speaking to see transcriptions here...
                  </p>
                ) : (
                  <div className="space-y-2">
                    {transcriptHistory.slice().reverse().map((item, idx) => (
                      <div 
                        key={idx} 
                        className="p-2 bg-slate-50 rounded text-sm border-l-2 border-emerald-400"
                      >
                        <p className="text-slate-700">{item.text}</p>
                        <p className="text-[10px] text-slate-400 mt-1">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Manual Send */}
            {!autobroadcast && currentText && (
              <Button 
                onClick={handleManualSend}
                className="w-full gap-2"
                disabled={!currentText.trim()}
              >
                <Send className="h-4 w-4" />
                Send to Attendees
              </Button>
            )}
          </div>

          {/* Right: Avatar Preview */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Attendee Preview
              </h3>
              <Badge variant="outline" className="text-xs">
                <Hand className="h-3 w-3 mr-1" />
                {signLangConfig.flag} {signLangConfig.name}
              </Badge>
            </div>
            
            {showPreview && (
              <div className="flex justify-center">
                <SignLanguageAvatarLive
                  text={currentText || interimText}
                  language={signLanguage}
                  size="large"
                  showCaption={true}
                  isListening={isListening}
                  statusText={isBroadcasting ? 'BROADCASTING' : isListening ? 'LISTENING' : 'READY'}
                  statusColor={isBroadcasting ? 'bg-red-500' : isListening ? 'bg-emerald-500' : 'bg-slate-500'}
                />
              </div>
            )}

            {/* Current Text Display */}
            <div className="p-4 bg-slate-900 rounded-lg min-h-[80px]">
              <p className="text-slate-400 text-xs mb-1">Current Text:</p>
              {currentText || interimText ? (
                <p className="text-white text-lg font-medium">
                  {currentText}
                  {interimText && (
                    <span className="text-slate-400 italic"> {interimText}</span>
                  )}
                </p>
              ) : (
                <p className="text-slate-500 italic">
                  {isListening ? 'Listening...' : 'Start speaking to see text here'}
                </p>
              )}
            </div>

            {/* Status Indicators */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className={`p-3 rounded-lg text-center ${isSessionActive ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                {isSessionActive ? (
                  <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                ) : (
                  <AlertCircle className="h-5 w-5 mx-auto text-slate-400 mb-1" />
                )}
                <p className="text-xs font-medium">Session</p>
              </div>
              <div className={`p-3 rounded-lg text-center ${isListening ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'}`}>
                {isListening ? (
                  <Mic className="h-5 w-5 mx-auto text-amber-600 mb-1 animate-pulse" />
                ) : (
                  <MicOff className="h-5 w-5 mx-auto text-slate-400 mb-1" />
                )}
                <p className="text-xs font-medium">Microphone</p>
              </div>
              <div className={`p-3 rounded-lg text-center ${isBroadcasting ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
                {isBroadcasting ? (
                  <Wifi className="h-5 w-5 mx-auto text-red-600 mb-1 animate-pulse" />
                ) : (
                  <WifiOff className="h-5 w-5 mx-auto text-slate-400 mb-1" />
                )}
                <p className="text-xs font-medium">Broadcast</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpeakerTranscriptionPanel;
